import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ESTADOS_FINALES = new Set(["egresado", "acreditado", "finalizado", "reprobado", "anulado", "retirado"]);
const ESTADO_LABELS = {
  inscrito: "Inscrito",
  cursando: "En curso",
  egresado: "Egresado",
  acreditado: "Acreditado",
  reprobado: "Reprobado",
  anulado: "Anulado",
  finalizado: "Finalizado",
  retirado: "Retirado",
};

const ESTADO_BADGE_CLASSES = {
  inscrito: "bg-info-subtle text-info-emphasis border border-info-subtle",
  cursando: "bg-primary-subtle text-primary-emphasis border border-primary-subtle",
  egresado: "bg-warning-subtle text-warning-emphasis border border-warning-subtle",
  acreditado: "bg-success-subtle text-success-emphasis border border-success-subtle",
  reprobado: "bg-danger-subtle text-danger-emphasis border border-danger-subtle",
  anulado: "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle",
  finalizado: "bg-dark-subtle text-dark-emphasis border border-dark-subtle",
  retirado: "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle",
};

const toEstadoLabel = (estado) => {
  const key = String(estado || "").trim().toLowerCase();
  return ESTADO_LABELS[key] || (estado ? String(estado) : "Sin estado");
};

const toEstadoBadgeClass = (estado) => {
  const key = String(estado || "").trim().toLowerCase();
  return ESTADO_BADGE_CLASSES[key] || "bg-light text-dark border";
};

const toDateLabel = (value) => {
  if (!value) return "-";
  const base = String(value).split("T")[0];
  const d = new Date(base);
  if (Number.isNaN(d.getTime())) return base || "-";
  return d.toLocaleDateString("es-EC", { year: "numeric", month: "2-digit", day: "2-digit" });
};

const toFormacionRow = (item = {}) => {
  const estadoRaw = String(item.estado || "").trim().toLowerCase();
  const curso = item.curso || item.curso_nombre || item.nombre_curso || item.cursoNombre || "-";
  const promocion = item.promocion || item.numero_promocion || item.promocion_nombre || item.promocionNombre || "-";
  const campania = item.campania || item.campania_nombre || item.convocatoria || item.convocatoria_nombre || "-";
  const nivel = item.nivel || item.nivel_nombre || item.nivelNombre || (item.nivel_id ? `Nivel ${item.nivel_id}` : "-");
  const centro = item.centro || item.centro_nombre || item.centro_alias || item.centroNombre || "-";
  const matriculaCodigo = item.codigo_matricula || (item.matricula_id ? `MAT-${item.matricula_id}` : "-");
  const rowKey = [item?.id || "-", item?.matricula_id || "-", item?.curso_id || "-", item?.fecha_inicio || "-"].join("|");

  return {
    rowKey,
    id: item.id || null,
    matriculaId: item.matricula_id || null,
    matriculaCodigo,
    cursoId: item.curso_id || null,
    curso,
    nivel,
    promocion,
    campania,
    centro,
    inicio: toDateLabel(item.fecha_inicio),
    fin: toDateLabel(item.fecha_fin),
    estadoRaw,
    estadoLabel: toEstadoLabel(item.estado),
    fechaAprobacion: toDateLabel(item.fecha_aprobacion),
    // Item original sin transformar — lo necesita el modal de editar/eliminar
    // (ids reales, fechas en formato ISO), a diferencia de los campos de arriba
    // que ya vienen formateados solo para mostrarse en la tabla.
    raw: item,
  };
};

export default function HistorialPanel({
  formaciones,
  historial,
  loading,
  error,
  canCreate = false,
  onNuevaFormacion,
  canEdit = false,
  canDelete = false,
  onEditar,
  onEliminar,
}) {
  const [menuOpenKey, setMenuOpenKey] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [detalleRow, setDetalleRow] = useState(null);
  const menuRef = useRef(null);

  const rows = useMemo(() => {
    const all = [...(Array.isArray(formaciones) ? formaciones : []), ...(Array.isArray(historial) ? historial : [])];
    const seen = new Set();
    const unique = [];

    all.forEach((item) => {
      const key = [item?.id, item?.matricula_id, item?.codigo_matricula, item?.curso_id, item?.fecha_inicio].join("|");
      if (seen.has(key)) return;
      seen.add(key);
      unique.push(toFormacionRow(item));
    });

    return unique;
  }, [formaciones, historial]);

  const formacionesActivas = useMemo(
    () => rows.filter((row) => !ESTADOS_FINALES.has(row.estadoRaw)),
    [rows]
  );
  const cursosTerminados = useMemo(
    () => rows.filter((row) => ESTADOS_FINALES.has(row.estadoRaw)),
    [rows]
  );

  const openRowMenu = (event, rowKey) => {
    event.preventDefault();
    event.stopPropagation();

    if (menuOpenKey === rowKey) {
      setMenuOpenKey(null);
      setMenuPosition(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 176;
    const itemCount = 1 + (canEdit ? 1 : 0) + (canDelete ? 1 : 0); // Ver informacion + Editar + Eliminar
    const menuHeight = 16 + itemCount * 38;
    const gap = 8;
    const viewportPadding = 8;

    let left = rect.right - menuWidth;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - menuWidth - viewportPadding));

    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - viewportPadding) {
      top = rect.top - menuHeight - gap;
    }
    top = Math.max(viewportPadding, top);

    setMenuOpenKey(rowKey);
    setMenuPosition({ top, left });
  };

  const openDetalle = (row) => {
    setDetalleRow(row);
    setMenuOpenKey(null);
    setMenuPosition(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const inMenu = menuRef.current?.contains(target);
      const fromTrigger = target instanceof Element && target.closest(".formaciones-menu-trigger");
      if (!inMenu && !fromTrigger) {
        setMenuOpenKey(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!menuOpenKey) return undefined;
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setMenuOpenKey(null);
        setMenuPosition(null);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [menuOpenKey]);

  const menuRow = useMemo(
    () => rows.find((row) => row.rowKey === menuOpenKey) || null,
    [rows, menuOpenKey]
  );

  const closeMenu = () => {
    setMenuOpenKey(null);
    setMenuPosition(null);
  };

  const handleEditar = (row) => {
    closeMenu();
    onEditar?.(row.raw);
  };

  const handleEliminar = (row) => {
    closeMenu();
    onEliminar?.(row.raw);
  };

  const quickMenu =
    menuOpenKey && menuPosition && menuRow
      ? createPortal(
          <div
            ref={menuRef}
            className="educacion-quick-menu"
            style={{ top: menuPosition.top, left: menuPosition.left, zIndex: 2300 }}
          >
            <button type="button" className="menu-item" onClick={() => openDetalle(menuRow)}>
              <i className="bi bi-eye me-2" />
              Ver informacion
            </button>
            {canEdit && (
              <button type="button" className="menu-item" onClick={() => handleEditar(menuRow)}>
                <i className="bi bi-pencil-square me-2" />
                Editar
              </button>
            )}
            {canDelete && (
              <button type="button" className="menu-item danger" onClick={() => handleEliminar(menuRow)}>
                <i className="bi bi-trash me-2" />
                Eliminar
              </button>
            )}
          </div>,
          document.body
        )
      : null;

  const renderTable = (items = [], emptyLabel = "Sin datos para mostrar.") => (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0 formaciones-table">
        <thead>
          <tr>
            <th>Codigo matricula</th>
            <th>Centro</th>
            <th>Curso</th>
            <th>Promocion</th>
            <th>Inicio</th>
            <th>Fin</th>
            <th>Estado de formacion</th>
            <th className="text-end">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item) => (
              <tr key={`form-row-${item.rowKey}`}>
                <td className="text-nowrap">{item.matriculaCodigo}</td>
                <td>{item.centro}</td>
                <td>{item.curso}</td>
                <td>{item.promocion}</td>
                <td className="text-nowrap">{item.inicio}</td>
                <td className="text-nowrap">{item.fin}</td>
                <td>
                  <span className={`badge rounded-pill fw-semibold ${toEstadoBadgeClass(item.estadoRaw)}`}>
                    {item.estadoLabel}
                  </span>
                </td>
                <td className="text-end">
                  <span
                    role="button"
                    tabIndex={0}
                    className="educacion-menu-icon-trigger formaciones-menu-trigger"
                    onClick={(event) => openRowMenu(event, item.rowKey)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openRowMenu(event, item.rowKey);
                      }
                    }}
                    title="Acciones"
                    aria-label="Acciones"
                  >
                    <i className="bi bi-three-dots-vertical" />
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={8} className="text-muted small py-2">
                {emptyLabel}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h6 className="mb-0">Historial de cursos realizados</h6>
        </div>
        {canCreate && onNuevaFormacion && (
          <button type="button" className="btn btn-primary btn-sm" onClick={onNuevaFormacion}>
            <i className="bi bi-plus-circle me-2" />
            Nueva formación
          </button>
        )}
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div className="text-center py-3">
          <div className="spinner-border text-primary" />
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h6 className="mb-2">Formaciones activas</h6>
                {renderTable(formacionesActivas, "Sin formaciones activas.")}
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h6 className="mb-2">Cursos terminados</h6>
                {renderTable(cursosTerminados, "Sin cursos terminados.")}
              </div>
            </div>
          </div>
        </div>
      )}
      {quickMenu}

      {detalleRow ? (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title">Información de curso</h5>
                  <button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setDetalleRow(null)} />
                </div>
                <div className="modal-body">
                  <dl className="row mb-0 small">
                    <dt className="col-sm-5">Codigo matricula</dt>
                    <dd className="col-sm-7">{detalleRow.matriculaCodigo}</dd>
                    <dt className="col-sm-5">Centro</dt>
                    <dd className="col-sm-7">{detalleRow.centro}</dd>
                    <dt className="col-sm-5">Campana</dt>
                    <dd className="col-sm-7">{detalleRow.campania}</dd>
                    <dt className="col-sm-5">Curso</dt>
                    <dd className="col-sm-7">{detalleRow.curso}</dd>
                    <dt className="col-sm-5">Nivel</dt>
                    <dd className="col-sm-7">{detalleRow.nivel}</dd>
                    <dt className="col-sm-5">Promocion</dt>
                    <dd className="col-sm-7">{detalleRow.promocion}</dd>
                    <dt className="col-sm-5">Inicio</dt>
                    <dd className="col-sm-7">{detalleRow.inicio}</dd>
                    <dt className="col-sm-5">Fin</dt>
                    <dd className="col-sm-7">{detalleRow.fin}</dd>
                    <dt className="col-sm-5">Fecha de aprobacion</dt>
                    <dd className="col-sm-7">{detalleRow.fechaAprobacion}</dd>
                    <dt className="col-sm-5">Estado</dt>
                    <dd className="col-sm-7">
                      <span className={`badge rounded-pill fw-semibold ${toEstadoBadgeClass(detalleRow.estadoRaw)}`}>
                        {detalleRow.estadoLabel}
                      </span>
                    </dd>
                  </dl>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setDetalleRow(null)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      ) : null}
    </>
  );
}
